(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer-custom';
  const ICON_SECTION_ID = 'cart-icon-bubble';

  // First match wins in the any-control fallback, so the quantity input leads.
  const CONTROL_ATTRIBUTES = [
    'data-gp-quantity-input',
    'data-gp-quantity-minus',
    'data-gp-quantity-plus',
    'data-gp-frequency-select',
    'data-gp-subscribe-toggle',
    'data-gp-remove',
  ];

  const ANY_CONTROL = CONTROL_ATTRIBUTES.map((name) => `[${name}]`).join(',');

  // Paint-only pending state; aria-checked stays the server's to report.
  const PENDING_CLASS = 'gp-cart-upsell__toggle--pending';

  const usable = (element) => (element && !element.disabled ? element : null);

  const escapeAttr = (value) =>
    typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');

  class GPCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this._rendering = false;
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });

      // scroll doesn't bubble, so listen in the capture phase.
      this.addEventListener(
        'scroll',
        (event) => {
          if (event.target.hasAttribute && event.target.hasAttribute('data-gp-upsell-track')) {
            this._syncUpsell();
          }
        },
        { capture: true, passive: true }
      );

      this._syncUpsell();

      this._bindCartIcon();
      this._releaseScrollLock();

      if (window.cartState) {
        window.cartState.addEventListener('cart:updated', () => this.applySections());
      }
    }

    // Temporary: stops Recharge's document wheel/touchmove lock cancelling scroll inside the drawer.
    _releaseScrollLock() {
      ['wheel', 'touchmove'].forEach((type) => {
        document.addEventListener(
          type,
          (event) => {
            const target = event.target;
            if (target instanceof Node && this.contains(target)) event.stopPropagation();
          },
          { capture: true, passive: true }
        );
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;

      setTimeout(() => this.classList.add('gp-cart-drawer--animate', 'gp-cart-drawer--active'));

      this.addEventListener(
        'transitionend',
        () => {
          const container = this.classList.contains('is-empty')
            ? this.querySelector('.gp-cart-drawer__empty') || this
            : this.querySelector('.gp-cart-drawer__panel') || this;
          const focusTarget =
            this.querySelector('.gp-cart-drawer__close') ||
            this.querySelector('.gp-cart-drawer__panel') ||
            container;

          if (typeof window.trapFocus === 'function') {
            window.trapFocus(container, focusTarget);
          } else if (focusTarget && focusTarget.focus) {
            focusTarget.focus();
          }
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');

      if (window.cartState) window.cartState.refresh();
    }

    close() {
      this.classList.remove('gp-cart-drawer--active');

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.activeElement);
      } else if (this.activeElement && this.activeElement.focus) {
        this.activeElement.focus();
      }

      document.body.classList.remove('overflow-hidden');
    }

    get isOpen() {
      return this.classList.contains('gp-cart-drawer--active');
    }

    _onClick(event) {
      const target = event.target;

      if (target.closest('[data-gp-overlay]') || target.closest('[data-gp-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const remove = target.closest('[data-gp-remove]');
      if (remove) {
        event.preventDefault();
        this._setQuantity(remove.closest('[data-gp-line]'), 0);
        return;
      }

      const subscribe = target.closest('[data-gp-subscribe-toggle]');
      if (subscribe) {
        event.preventDefault();
        this._toggleSubscription(subscribe);
        return;
      }

      const upsellAdd = target.closest('[data-gp-upsell-add]');
      if (upsellAdd) {
        event.preventDefault();
        this._addUpsell(upsellAdd);
        return;
      }

      const upsellArrow = target.closest('[data-gp-upsell-prev], [data-gp-upsell-next]');
      if (upsellArrow) {
        event.preventDefault();
        this._scrollUpsell(upsellArrow, upsellArrow.hasAttribute('data-gp-upsell-prev') ? -1 : 1);
        return;
      }

      const minus = target.closest('[data-gp-quantity-minus]');
      const plus = target.closest('[data-gp-quantity-plus]');
      if (!minus && !plus) return;

      event.preventDefault();

      const item = (minus || plus).closest('[data-gp-line]');
      const input = item && item.querySelector('[data-gp-quantity-input]');
      if (!input) return;

      const step = parseInt(input.getAttribute('step'), 10) || 1;
      const current = parseInt(input.value, 10) || 0;
      const next = minus ? current - step : current + step;

      this._setQuantity(item, Math.max(0, next));
    }

    _onChange(event) {
      const frequency = event.target.closest('[data-gp-frequency-select]');
      if (frequency) {
        this._setSellingPlan(frequency);
        return;
      }

      const input = event.target.closest('[data-gp-quantity-input]');
      if (!input) return;

      const item = input.closest('[data-gp-line]');
      const next = parseInt(input.value, 10);

      if (Number.isNaN(next) || next < 0) {
        input.value = input.getAttribute('data-gp-quantity-value');
        return;
      }

      this._setQuantity(item, next);
    }

    // By line key, not index: BOGOS can shift line indexes between render and click.
    _setQuantity(item, quantity) {
      if (!item || !window.cartState) return;

      const key = item.getAttribute('data-gp-key');
      if (!key) return;

      this._setBusy(item, true);
      this._clearErrors();

      window.cartState
        .change({ id: key, quantity })
        .catch((error) => this._showLineError(item, error))
        .finally(() => this._setBusy(item, false));
    }

    // Quantity comes from the server-rendered value, not the possibly uncommitted input.
    _setSellingPlan(select) {
      const item = select.closest('[data-gp-line]');
      if (!item || !window.cartState) return;

      const key = item.getAttribute('data-gp-key');
      const sellingPlan = select.value;
      const previous = select.getAttribute('data-gp-frequency-value');

      if (!key || sellingPlan === previous) return;

      const input = item.querySelector('[data-gp-quantity-input]');
      const quantity = parseInt(input && input.getAttribute('data-gp-quantity-value'), 10);

      this._setBusy(item, true);
      this._clearErrors();

      window.cartState
        .change({
          id: key,
          quantity: Number.isNaN(quantity) ? 1 : quantity,
          selling_plan: sellingPlan,
        })
        .catch((error) => {
          if (previous) select.value = previous;
          this._showLineError(item, error);
        })
        .finally(() => this._setBusy(item, false));
    }

    _toggleSubscription(toggle) {
      const item = toggle.closest('[data-gp-line]');
      if (!item || !window.cartState) return;

      const key = item.getAttribute('data-gp-key');
      if (!key) return;

      const isOn = toggle.getAttribute('aria-checked') === 'true';
      const sellingPlan = isOn ? '' : toggle.getAttribute('data-gp-selling-plan');
      if (!isOn && !sellingPlan) return;

      const line = this.querySelector(`[data-gp-line][data-gp-key="${CSS.escape(key)}"] [data-gp-quantity-input]`);
      const quantity = parseInt(line && line.getAttribute('data-gp-quantity-value'), 10);

      toggle.classList.toggle(PENDING_CLASS, !isOn);

      this._setBusy(item, true);
      this._clearErrors();

      window.cartState
        .change({
          id: key,
          quantity: Number.isNaN(quantity) ? 1 : quantity,
          selling_plan: sellingPlan,
        })
        .catch((error) => {
          // A failure triggers no re-render, so undo the optimistic paint.
          toggle.classList.toggle(PENDING_CLASS, isOn);
          this._showLineError(item, error);
        })
        .finally(() => this._setBusy(item, false));
    }

    // Only 422 text is shopper-facing; body status can be a string, e.g. "bad_request".
    _showLineError(item, error) {
      const cartError = error && error.cartError;
      const shopperFacing = cartError && cartError.status === 422;

      const message =
        (shopperFacing && (cartError.description || cartError.message)) ||
        (window.cartStrings && window.cartStrings.error) ||
        'There was an error while updating your cart. Please try again.';

      const lineError = item && item.querySelector('[data-gp-line-error]');
      const cartErrors = this.querySelector('[data-gp-cart-errors]');
      if (lineError) lineError.textContent = message;
      else if (cartErrors) cartErrors.textContent = message;
    }

    _setBusy(item, busy) {
      this.classList.toggle('gp-cart-drawer--busy', busy);
      if (item) item.classList.toggle('gp-cart-item--busy', busy);
    }

    _clearErrors() {
      this.querySelectorAll('[data-gp-line-error], [data-gp-cart-errors]').forEach((el) => {
        el.textContent = '';
      });
    }

    // Temporary: document capture beats Recharge's capture listener on #cart-icon-bubble.
    _bindCartIcon() {
      if (document.documentElement.dataset.gpDrawerBound) return;
      document.documentElement.dataset.gpDrawerBound = 'true';

      const isCartIcon = (event) => {
        const target = event.target;
        return !!(target && target.closest && target.closest('#cart-icon-bubble'));
      };

      const takeOver = (event) => {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.open(event.target.closest('#cart-icon-bubble'));
      };

      document.addEventListener(
        'click',
        (event) => {
          if (isCartIcon(event)) takeOver(event);
        },
        true
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.code && event.code.toUpperCase() === 'SPACE' && isCartIcon(event)) takeOver(event);
        },
        true
      );
    }

    // data-gp-key rotates on frequency/subscribe changes, so restore matches key, then variant, then position.
    _captureFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;

      const control = CONTROL_ATTRIBUTES.find((name) => active.hasAttribute(name));
      if (!control) return null;

      const line = active.closest('[data-gp-line]');
      const key = line ? line.getAttribute('data-gp-key') : null;

      // The upsell block carries data-gp-line too, so count only product lines.
      const lines = Array.prototype.slice.call(this.querySelectorAll('.gp-cart-item[data-gp-key]'));
      const position = key ? lines.findIndex((el) => el.getAttribute('data-gp-key') === key) : -1;

      return {
        control,
        key,
        variant: line ? line.getAttribute('data-gp-variant') : null,
        position,
      };
    }

    _restoreFocus(token) {
      if (!token) return;

      let target = null;

      if (token.key) {
        target = usable(this.querySelector(`[data-gp-key="${escapeAttr(token.key)}"] [${token.control}]`));
      }

      if (!target && token.variant) {
        target = usable(
          this.querySelector(`[data-gp-variant="${escapeAttr(token.variant)}"] [${token.control}]`)
        );
      }

      if (!target && token.variant) {
        const line = this.querySelector(`.gp-cart-item[data-gp-variant="${escapeAttr(token.variant)}"]`);
        target = line && usable(line.querySelector(ANY_CONTROL));
      }

      if (!target && token.position >= 0) {
        const lines = this.querySelectorAll('.gp-cart-item[data-gp-key]');
        const neighbour = lines[Math.min(token.position, lines.length - 1)];
        if (neighbour) {
          target =
            usable(neighbour.querySelector(`[${token.control}]`)) || usable(neighbour.querySelector(ANY_CONTROL));
        }
      }

      if (!target) {
        target = this.querySelector('.gp-cart-drawer__close') || this.querySelector('.gp-cart-drawer__panel');
      }

      if (!target || typeof target.focus !== 'function') return;

      target.focus();

      if (target.hasAttribute && target.hasAttribute('data-gp-quantity-input')) {
        try {
          target.select();
        } catch (error) {
          /* not selectable — focus alone is enough */
        }
      }
    }

    // Disabled until the re-render replaces it, since the add isn't idempotent.
    _addUpsell(button) {
      if (!window.cartState || button.disabled) return;

      const variantId = parseInt(button.getAttribute('data-gp-upsell-variant'), 10);
      if (!variantId) return;

      const card = button.closest('.gp-cart-upsell-card');

      button.disabled = true;
      this._setBusy(card, true);
      this._clearErrors();

      window.cartState
        .add([{ id: variantId, quantity: 1 }])
        .catch((error) => {
          this._showLineError(card, error);
          button.disabled = false;
        })
        .finally(() => this._setBusy(card, false));
    }

    _scrollUpsell(arrow, direction) {
      const strip = arrow.closest('.gp-cart-upsell-strip');
      const track = strip && strip.querySelector('[data-gp-upsell-track]');
      if (!track) return;

      const card = track.querySelector('.gp-cart-upsell-card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;

      track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    // 1px tolerance: scrollLeft is fractional on zoomed/hidpi displays.
    _syncUpsell() {
      this.querySelectorAll('.gp-cart-upsell-strip').forEach((strip) => {
        const track = strip.querySelector('[data-gp-upsell-track]');
        const prev = strip.querySelector('[data-gp-upsell-prev]');
        const next = strip.querySelector('[data-gp-upsell-next]');
        if (!track) return;

        const max = track.scrollWidth - track.clientWidth;

        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
      });
    }

    applySections() {
      if (this._rendering) return Promise.resolve();
      this._rendering = true;

      const url = `${window.location.pathname}?sections=${SECTION_ID},${ICON_SECTION_ID}`;

      return fetch(url, { credentials: 'same-origin' })
        .then((response) => response.json())
        .then((sections) => this._swap(sections))
        .catch((error) => console.warn('[gp-cart] section render failed', error))
        .finally(() => {
          this._rendering = false;
        });
    }

    // Swap the inner root, not the host, so open state survives.
    _swap(sections) {
      const focus = this._captureFocus();

      const upsellTrack = this.querySelector('[data-gp-upsell-track]');
      const upsellScroll = upsellTrack ? upsellTrack.scrollLeft : 0;

      const drawerHTML = sections[SECTION_ID];

      if (drawerHTML) {
        const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');
        const incomingHost = parsed.querySelector('gp-cart-drawer');
        const incomingRoot = parsed.querySelector('#GpCartDrawer');
        const currentRoot = this.querySelector('#GpCartDrawer');

        if (incomingRoot && currentRoot) {
          currentRoot.innerHTML = incomingRoot.innerHTML;
          this._runScripts(currentRoot);
        }

        if (incomingHost) {
          this.classList.toggle('is-empty', incomingHost.classList.contains('is-empty'));
        }
      }

      const iconHTML = sections[ICON_SECTION_ID];
      const iconTarget = document.querySelector('#cart-icon-bubble');

      if (iconHTML && iconTarget) {
        const parsed = new DOMParser().parseFromString(iconHTML, 'text/html');
        const incomingIcon = parsed.querySelector('.shopify-section') || parsed.body;
        if (incomingIcon) iconTarget.innerHTML = incomingIcon.innerHTML;
      }

      if (upsellScroll) {
        const incomingTrack = this.querySelector('[data-gp-upsell-track]');
        if (incomingTrack) incomingTrack.scrollLeft = upsellScroll;
      }

      this._syncUpsell();
      this._restoreFocus(focus);
    }

    // innerHTML never runs scripts; re-insert them so embeds like the Reviews.io bar reload.
    _runScripts(root) {
      Array.prototype.slice.call(root.querySelectorAll('script')).forEach((original) => {
        const script = document.createElement('script');

        Array.prototype.slice.call(original.attributes).forEach((attribute) => {
          script.setAttribute(attribute.name, attribute.value);
        });

        script.textContent = original.textContent;
        original.parentNode.replaceChild(script, original);
      });
    }
  }

  if (!customElements.get('gp-cart-drawer')) {
    customElements.define('gp-cart-drawer', GPCartDrawer);
  }

  window.GPCartDrawer = GPCartDrawer;
})();
